import React, { useCallback } from 'react';
import { useSpring, animated } from 'react-spring';
import { useInView } from 'react-intersection-observer';

const ParticlesBackground = () => {
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  const springProps = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px)' : 'translateY(50px)',
    config: { mass: 1, tension: 280, friction: 120 },
  });

  const generateParticles = useCallback(() => {
    const particles = [];
    for (let i = 0; i < 50; i++) {
      particles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 1,
        duration: Math.random() * 20 + 10,
        delay: Math.random() * 5,
      });
    }
    return particles;
  }, []);

  const particles = generateParticles();

  return (
    <div ref={ref} className="fixed inset-0 overflow-hidden pointer-events-none">
      <animated.div style={springProps} className="relative w-full h-full">
        {/* Particules flottantes */}
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full opacity-20"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `hsl(${Math.random() * 60 + 40}, 70%, 60%)`,
              animation: `float ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
            }}
          />
        ))}

        {/* Grille de points */}
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full bg-solar-pattern" />
        </div>

        {/* Ondes radiales */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full border border-solar-400/20 animate-pulse-slow" />
        <div className="absolute top-1/4 left-1/4 w-80 h-80 rounded-full border border-sky-400/20 animate-pulse-slow" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full border border-green-400/20 animate-pulse-slow" style={{ animationDelay: '2s' }} />

        {/* Gradient circulaire */}
        <div className="absolute top-1/2 right-1/4 w-96 h-96 rounded-full bg-gradient-radial from-solar-400/10 via-transparent to-transparent animate-spin-slow" />
        <div className="absolute bottom-1/4 left-1/2 w-80 h-80 rounded-full bg-gradient-radial from-sky-400/10 via-transparent to-transparent animate-spin-slow" style={{ animationDirection: 'reverse' }} />

        {/* Lignes de connexion */}
        <svg className="absolute inset-0 w-full h-full opacity-10">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fde047" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#22c55e" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          {[...Array(8)].map((_, i) => (
            <line
              key={i}
              x1={Math.random() * 100}
              y1={Math.random() * 100}
              x2={Math.random() * 100}
              y2={Math.random() * 100}
              stroke="url(#lineGradient)"
              strokeWidth="1"
              className="animate-pulse"
              style={{ animationDelay: `${i * 0.5}s` }}
            />
          ))}
        </svg>

        {/* Effet de brillance */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
      </animated.div>
    </div>
  );
};

export default ParticlesBackground; 